import React, { useState, useRef, useEffect } from 'react'

import { Icon, TriangleDown, TriangleUp } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { array, object, func, string, bool } from 'prop-types'

import CGTStandardFilterOption from './CGTStandardFilterOption'

function CGTStandardFilter({
  options,
  onSelectedChanged,
  selected,
  placeholder = "CGT Standard",
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [expandedItems, setExpandedItems] = useState(new Set())
  const [processedValue, setProcessedValue] = useState(null)
  const dropdownRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  const toggleExpanded = (itemValue, event) => {
    event.stopPropagation()
    setExpandedItems(prevExpanded => {
      if (prevExpanded.has(itemValue)) {
        return new Set([...prevExpanded].filter(item => item !== itemValue))
      }
      return new Set([...prevExpanded, itemValue])
    })
  }

  const handleItemClick = (item) => {
    const isCurrentlySelected = item.value in selected

    if (isCurrentlySelected) {
      setExpandedItems(new Set())
      setProcessedValue(null)
      onSelectedChanged({})
    } else {
      setExpandedItems(new Set())
      setProcessedValue(null)

      const newSelected = {}
      if (item.subitems && item.subitems.length > 0 && item.subitems.some(sub => sub.value)) {
        newSelected[item.value] = []
      } else {
        newSelected[item.value] = true
      }
      onSelectedChanged(newSelected)
    }
  }

  const handleSubitemClick = (parentValue, subitem) => {
    const newSelected = { ...selected }

    if (!newSelected[parentValue]) {
      newSelected[parentValue] = []
    }

    const subitems = [...newSelected[parentValue]]
    const index = subitems.indexOf(subitem.value)

    if (index > -1) {
      subitems.splice(index, 1)
    } else {
      subitems.push(subitem.value)
    }

    newSelected[parentValue] = subitems
    onSelectedChanged(newSelected)
  }

  const isItemSelected = (item) => {
    return item.value in selected
  }

  const isSubitemSelected = (parentValue, subitem) => {
    if (!selected[parentValue] || !Array.isArray(selected[parentValue])) {
      return false
    }
    return selected[parentValue].includes(subitem.value)
  }

  const getSelectedCount = () => {
    const selectedKeys = Object.keys(selected)
    if (selectedKeys.length === 0) return 0

    if (processedValue !== null) {
      return 1
    }

    let count = 0
    selectedKeys.forEach(key => {
      const value = selected[key]
      if (value === true) {
        count += 1
      } else if (Array.isArray(value)) {
        count += 1 + value.length
      }
    })

    return count
  }

  return (
    <div ref={dropdownRef} className="standard-filters">
      <div className="standard-filters__trigger" onClick={toggleDropdown}>
        <span className="standard-filters__trigger-text">{placeholder}</span>
        <Icon svgComponent={isOpen ? TriangleUp : TriangleDown} color="dark" />
        {getSelectedCount() > 0 && (
          <span className="standard-filters__trigger-count">{getSelectedCount()}</span>
        )}
      </div>

      {isOpen && (
        <div className="standard-filters__dropdown">
          <div className="standard-filters__dropdown-header">
            <span className="standard-filters__dropdown-header-title">CGT Standard</span>
            <p className="standard-filters__dropdown-header-subtitle">
              Filter models based on CGT Standard verification status
            </p>
          </div>

          <div className="standard-filters__dropdown-options">
            {options.map((option) => {
              const showSubitems = option.subitems &&
                isItemSelected(option) &&
                expandedItems.has(option.value)

              return (
                <div key={option.value}>
                  <CGTStandardFilterOption
                    option={option}
                    isSelected={isItemSelected(option)}
                    isExpanded={expandedItems.has(option.value)}
                    disabled={disabled}
                    onClick={handleItemClick}
                    onToggleExpanded={toggleExpanded}
                  />

                  {showSubitems && (
                    <div>
                      {option.subitems.map((subitem) => (
                        <CGTStandardFilterOption
                          key={subitem.value}
                          option={subitem}
                          isSelected={isSubitemSelected(option.value, subitem)}
                          disabled={disabled}
                          onClick={handleSubitemClick}
                          isSubitem
                          parentValue={option.value}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

CGTStandardFilter.propTypes = {
  options: array.isRequired,
  selected: object.isRequired,
  onSelectedChanged: func.isRequired,
  placeholder: string,
  disabled: bool,
}

export default CGTStandardFilter
